import {Loader} from '@mantine/core';
import {useEffect, useRef, useState} from 'react';
import {FaCompass} from 'react-icons/fa';
import {COLOR_PRIMARY_1} from '../../../../config/constants/colorCodes';
import {
  INITIAL_TAG_LIST,
  TAG_ID_FOLLOWED,
} from '../../../../core/entities/community/constants';
import {
  useGetCommunityTagList,
  useInfiniteGetCommunityTopicList,
} from '../../../../core/repositories/communityRepository';
import {CommunitySearchForm} from './CommunitySearchForm';
import {PostFormAuthBox} from './PostFormAuthBox';
import {PostTopicForm} from './PostTopicForm';
import {PostButton} from './Presenter/PostButton';
import {TopicListItem} from './Presenter/TopicListItem';

const TOPIC_LIST_PAGE_LIMIT = 20;

type Props = {
  selectedTagId: number | string;
  setSelectedTagId: (tagId: string) => void;
  communityNickname: string;
  isSignedIn: boolean;
  isStudentVerificationRequired: boolean;
};

export const CommunityQuestionListPcComponent: React.FC<Props> = ({
  selectedTagId,
  communityNickname,
  isSignedIn,
  isStudentVerificationRequired,
}: Props) => {
  const [searchWords, setSearchWords] = useState<string>('');
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const postTopicFormRef = useRef<HTMLDivElement>(null);

  const {data, isFetching, fetchNextPage} = useInfiniteGetCommunityTopicList({
    tagID: Number.isNaN(Number.parseInt(selectedTagId as string, 10))
      ? undefined
      : Number.parseInt(selectedTagId as string, 10),
    keyword: searchWords ?? undefined,
    followed: selectedTagId === TAG_ID_FOLLOWED,
    limit: TOPIC_LIST_PAGE_LIMIT,
  });

  const {data: communityPopularTagList} = useGetCommunityTagList({
    isPopular: false,
  });

  const selectedTagName =
    selectedTagId !== '' && communityPopularTagList?.ok
      ? [
          ...INITIAL_TAG_LIST,
          ...communityPopularTagList.value.communityTags,
        ].find((tag) => String(tag.id) === selectedTagId)?.name
      : '';

  useEffect(() => {
    if (
      data &&
      data.pages.length > 0 &&
      data.pages[0]?.ok &&
      data.pages[0].value.hitCount - data.pages.length * TOPIC_LIST_PAGE_LIMIT >
        1
    ) {
      setHasNextPage(true);
      return;
    }

    setHasNextPage(false);
  }, [data]);

  return (
    <>
      <div className="bg-white rounded-12 shadow-modal">
        <div className="p-16 border-b-[1px] border-line border-collapse">
          {!selectedTagName && (
            <p className="text-base text-black-1">
              就活の悩み・疑問を投稿しよう。内定者や就活生同士で業界研究・選考対策などを語り合う場です。
            </p>
          )}
          <div
            className={`flex justify-between items-center ${selectedTagName ? '' : 'mt-12'}`}
          >
            {selectedTagName ? (
              <h1 className="text-[24px] text-black-1 font-bold leading">
                {selectedTagName}
              </h1>
            ) : (
              <div className="flex items-center w-[calc(100%-124px)] mr-8">
                <div className="mr-8">
                  <FaCompass size={20} fill={COLOR_PRIMARY_1} />
                </div>
                <h1 className="text-[24px] text-black-1 font-bold leading">
                  外資就活コミュニティ
                </h1>
              </div>
            )}
            <PostButton
              onClick={() => {
                if (!postTopicFormRef.current) return;
                postTopicFormRef.current.scrollIntoView({behavior: 'smooth'});
              }}
            />
          </div>
        </div>

        <div className="p-16 border-b-[1px] border-line border-collapse">
          <CommunitySearchForm
            placeholder="トピックを検索"
            setSearchWords={setSearchWords}
          />
        </div>

        <div className="p-16">
          {data && data?.pages.length > 0 ? (
            <ul>
              {data.pages.map(
                (page) =>
                  page.ok &&
                  page.value.topics.map((topic) => (
                    <TopicListItem
                      key={`topic-${topic.id}`}
                      topic={topic}
                      isSignedIn={isSignedIn}
                    />
                  )),
              )}
            </ul>
          ) : (
            <div>条件に一致するトピックがありません</div>
          )}
          {isFetching && (
            <div className="flex justify-center items-center h-[200px]">
              <Loader color="gray" />
            </div>
          )}
        </div>
      </div>
      {data && data?.pages.length > 0 && !isFetching && hasNextPage && (
        <div className="mt-16 flex justify-center w-full">
          <button
            type="button"
            className="border-[2px] border-primary-1 border-collapse hover:opacity-80 py-8 px-16 rounded-full"
            onClick={async () => {
              await fetchNextPage();
            }}
          >
            <span className="text-base text-primary-1 font-bold leading">
              さらに読み込む
            </span>
          </button>
        </div>
      )}
      <div ref={postTopicFormRef}>
        {isSignedIn ? (
          <PostTopicForm communityNickname={communityNickname} />
        ) : (
          <PostFormAuthBox
            isStudentVerificationRequired={isStudentVerificationRequired}
          />
        )}
      </div>
    </>
  );
};
